import { Box, Typography, Chip, Dialog } from '@mui/material';
import { useState } from 'react';
import { Datagrid, FunctionField, useTranslate } from 'react-admin';
import XDateField from '../XDateField';
import XTextField from '../XTextField';
import { getActionColor, getMethodColor } from './auditLogUtils';
import DiffViewerDialog from './DiffViewerDialog';
import { parseAuditJson } from './auditPayload';
import AuditLogHostCell from './AuditLogHostCell';

interface AuditLogTableProps {
  showResource?: boolean; // Để hiển thị "Resource" column (cho audit-logs List)
  showId?: boolean; // Để hiển thị "ID" column (cho audit-logs List)
  showOldData?: boolean; // Để hiển thị "Old Data" column (cho audit-logs List)
  showNewData?: boolean; // Để hiển thị "New Data" column (cho audit-logs List)
  showMethod?: boolean; // Để hiển thị "Method" column (cho audit-logs List)
  showIp?: boolean; // Để hiển thị "IP" column (cho audit-logs List)
}

const PREVIEW_LENGTH = 50;

const AuditLogTable = ({
  showResource = false,
  showId = false,
  showOldData = false,
  showNewData = false,
  showMethod = false,
  showIp = false,
}: AuditLogTableProps) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [diffDialogData, setDiffDialogData] = useState<{ oldData: any; newData: any } | null>(null);
  const translate = useTranslate();
  return (
    <Box sx={{ overflow: 'auto' }}>
      <Datagrid
        bulkActionButtons={false}
        sx={{
          '& .MuiTableCell-root': {
            whiteSpace: 'nowrap',
          },
        }}
      >
        <FunctionField
          label={translate('module.auditLogs.user')}
          render={(log: any) => (
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {log.userName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {log.email}
              </Typography>
            </Box>
          )}
        />
        <FunctionField
          label={translate('module.auditLogs.action')}
          render={(log: any) => (
            <Chip
              label={log.action?.toUpperCase()}
              color={getActionColor(log.action)}
              size="small"
            />
          )}
        />
        {showMethod && (
          <FunctionField
            label={translate('module.auditLogs.method')}
            render={(log: any) => (
              <Chip
                label={log.method}
                color={getMethodColor(log.method)}
                size="small"
                variant="outlined"
              />
            )}
          />
        )}

        {showResource && (
          <FunctionField
            label={translate('module.auditLogs.resource')}
            render={(log: any) => <Typography variant="body2">{log.resource}</Typography>}
          />
        )}

        {showId && (
          <FunctionField
            label={translate('module.auditLogs.dataId')}
            render={(log: any) => <XTextField text={log.id} short />}
          />
        )}

        <FunctionField
          label={translate('module.auditLogs.changes')}
          render={(log: any) => {
            const changes = typeof log.changes === 'string' ? JSON.parse(log.changes) : log.changes;
            const oldData = typeof log.oldData === 'string' ? JSON.parse(log.oldData || '{}') : (log.oldData || {});
            const newData = typeof log.newData === 'string' ? JSON.parse(log.newData || '{}') : (log.newData || {});

            return (
              <Typography
                variant="caption"
                onClick={() => setDiffDialogData({ oldData, newData })}
                sx={{
                  fontSize: '0.75rem',
                  maxWidth: '200px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  color: 'primary.main',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {JSON.stringify(changes).slice(0, 50)}...
              </Typography>
            );
          }}
        />

        {showOldData && (
          <FunctionField
            label={translate('module.auditLogs.oldData')}
            render={(log: any) => {
              const oldData =
                typeof log.oldData === 'string' ? JSON.parse(log.oldData) : log.oldData;
              const oldDataStr = JSON.stringify(oldData, null, 2);

              return (
                <>
                  <Typography
                    variant="caption"
                    onClick={() => setExpandedId(`oldData-${log.id}`)}
                    sx={{
                      fontSize: '0.75rem',
                      maxWidth: '200px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      color: 'primary.main',
                    }}
                  >
                    {JSON.stringify(oldData).slice(0, 50)}...
                  </Typography>
                  <Dialog
                    open={expandedId === `oldData-${log.id}`}
                    onClose={() => setExpandedId(null)}
                  >
                    <Box sx={{ p: 2 }}>
                      <Typography component="pre" sx={{ fontSize: '0.75rem', overflow: 'auto' }}>
                        {oldDataStr}
                      </Typography>
                    </Box>
                  </Dialog>
                </>
              );
            }}
          />
        )}

        {showNewData && (
          <FunctionField
            label={translate('module.auditLogs.newData')}
            render={(log: any) => {
              const newData =
                typeof log.newData === 'string' ? JSON.parse(log.newData) : log.newData;
              const newDataStr = JSON.stringify(newData, null, 2);
              return (
                <>
                  <Typography
                    variant="caption"
                    onClick={() => setExpandedId(`newData-${log.id}`)}
                    sx={{
                      fontSize: '0.75rem',
                      maxWidth: '200px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      color: 'primary.main',
                    }}
                  >
                    {JSON.stringify(newData).slice(0, 50)}...
                  </Typography>
                  <Dialog
                    open={expandedId === `newData-${log.id}`}
                    onClose={() => setExpandedId(null)}
                  >
                    <Box sx={{ p: 2 }}>
                      <Typography component="pre" sx={{ fontSize: '0.75rem', overflow: 'auto' }}>
                        {newDataStr}
                      </Typography>
                    </Box>
                  </Dialog>
                </>
              );
            }}
          />
        )}

        {(['requestBody', 'responseBody'] as const).map((field) => (
          <FunctionField
            key={field}
            label={translate(`module.auditLogs.${field}`)}
            render={(log: any) => {
              const payload = parseAuditJson(log[field]);
              if (payload === null) return <Typography variant="caption">—</Typography>;
              const dialogId = `${field}-${log.id}`;
              const text = typeof payload === 'string' ? payload : JSON.stringify(payload);
              return (
                <>
                  <Typography
                    variant="caption"
                    onClick={() => setExpandedId(dialogId)}
                    sx={{
                      fontSize: '0.75rem',
                      maxWidth: '200px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      color: 'primary.main',
                    }}
                  >
                    {text.slice(0, PREVIEW_LENGTH)}
                    {text.length > PREVIEW_LENGTH ? '...' : ''}
                  </Typography>
                  <Dialog open={expandedId === dialogId} onClose={() => setExpandedId(null)}>
                    <Box sx={{ p: 2 }}>
                      <Typography component="pre" sx={{ fontSize: '0.75rem', overflow: 'auto' }}>
                        {typeof payload === 'string' ? payload : JSON.stringify(payload, null, 2)}
                      </Typography>
                    </Box>
                  </Dialog>
                </>
              );
            }}
          />
        ))}

        <FunctionField
          label={translate('module.auditLogs.statusCode')}
          render={(log: any) => {
            const error: any = parseAuditJson(log.error);
            if (log.statusCode == null && !error) return <Typography variant="caption">—</Typography>;
            const failed = typeof log.statusCode === 'number' && log.statusCode >= 400;
            return (
              <Box>
                {log.statusCode != null && (
                  <Chip
                    label={log.statusCode}
                    color={failed ? 'error' : 'default'}
                    size="small"
                    variant="outlined"
                  />
                )}
                {error && (
                  <Typography variant="caption" color="error" display="block">
                    {typeof error === 'string'
                      ? error
                      : [error.code, Array.isArray(error.message) ? error.message.join('; ') : error.message]
                          .filter(Boolean)
                          .join(': ')}
                  </Typography>
                )}
              </Box>
            );
          }}
        />

        {showIp && (
          <FunctionField
            label={translate('module.auditLogs.ip')}
            render={(log: any) => <Typography variant="caption">{log.ip}</Typography>}
          />
        )}
        <FunctionField
          label={translate('module.auditLogs.host')}
          render={(log: any) => (
            <AuditLogHostCell host={log.host} forwardedHost={log.forwardedHost} origin={log.origin} />
          )}
        />
        <FunctionField
          label={translate('module.auditLogs.time')}
          render={(log: any) => <XDateField fullDate value={log.createdAt} />}
        />
      </Datagrid>
      <DiffViewerDialog
        open={!!diffDialogData}
        onClose={() => setDiffDialogData(null)}
        oldData={diffDialogData?.oldData}
        newData={diffDialogData?.newData}
      />
    </Box>
  );
};

export default AuditLogTable;
